import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, limit, onSnapshot, orderBy, query, Timestamp, doc, getDoc, getDocs, where, startAfter } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAppData } from '../../providers/AppDataProvider';
import { useEmailPanel, PanelEmailData } from '../mail/contexts/EmailPanelContext';
import { parseAddresses } from '../mail/utils/replyRecipients';
import JobEditModal from '../job-summary/JobEditModal';
import JobWorkspaceModal from '../job-summary/JobWorkspaceModal';

type EntityRef = { type: 'email' | 'job' | 'task' | 'chat'; id: string; label?: string };

type ActivityEvent = {
  id: string;
  customerId: string;
  eventType: string;
  primaryEntity: EntityRef;
  relatedEntity?: EntityRef;
  title?: string;
  snippet?: string;
  actor?: string;
  direction?: 'in' | 'out';
  linkUrl?: string;
  timestamp?: Timestamp | Date | { seconds: number; nanoseconds: number } | null;
};

type CustomerDoc = { name?: string; company?: string; contacts?: { name?: string; email?: string; phone?: string }[] };

const formatDateTime = (ts?: ActivityEvent['timestamp']) => {
  if (!ts) return '';
  try {
    let date: Date | null = null;
    if (ts instanceof Timestamp) date = ts.toDate();
    else if (ts instanceof Date) date = ts;
    else if (typeof ts === 'object' && ts && 'seconds' in ts) date = new Date(ts.seconds * 1000);
    return date ? date.toLocaleString() : '';
  } catch {
    return '';
  }
};

const getEventLabel = (eventType: string): string => {
  switch (eventType) {
    case 'email.received': return 'Email Received';
    case 'email.sent': return 'Email Sent';
    case 'chat.activity': return 'Chat Activity';
    case 'job.created': return 'Job Created';
    case 'job.status': return 'Job Status Changed';
    case 'task.created': return 'Task Created';
    case 'task.updated': return 'Task Updated';
    default: return eventType;
  }
};

export const CustomerActivityPage: React.FC = () => {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Removed incremental pagination in favor of page size selection
  const [customerNameById, setCustomerNameById] = useState<Record<string, string>>({});
  const [customerSearchById, setCustomerSearchById] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [dateStart, setDateStart] = useState<string>('');
  const [dateEnd, setDateEnd] = useState<string>('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [pageSize, setPageSize] = useState<number>(50);
  const [sortBy, setSortBy] = useState<'date_desc' | 'date_asc'>('date_desc');
  // Customer list comes straight from the centralized AppDataProvider (the
  // same source the retired ChatContext cache was derived from).
  const { customers: customerListCache } = useAppData();
  const { openEmailPanel } = useEmailPanel();
  const navigate = useNavigate();

  // Local modal state to open job/task views without leaving this page
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showJobEditModal, setShowJobEditModal] = useState(false);
  const [initialTaskId, setInitialTaskId] = useState<string | undefined>(undefined);
  const [copiedEntityId, setCopiedEntityId] = useState<string | null>(null);

  const formatRelative = (ts?: ActivityEvent['timestamp']): string => {
    try {
      let date: Date | null = null;
      if (!ts) return '';
      if (ts instanceof Timestamp) date = ts.toDate();
      else if (ts instanceof Date) date = ts;
      else if (typeof ts === 'object' && ts && 'seconds' in ts) date = new Date(ts.seconds * 1000);
      if (!date) return '';
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return 'now';
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.floor(diffHr / 24);
      return `${diffDay}d ago`;
    } catch {
      return '';
    }
  };

  const getMobileTitle = (text: string): string => {
    if (!text) return '';
    const limit = 30;
    return text.length > limit ? `${text.slice(0, limit)}…` : text;
  };

  useEffect(() => {
    setIsLoading(true);
    const base: any[] = [collection(db, 'customer_activity')];
    const dir = sortBy === 'date_desc' ? 'desc' : 'asc';
    const constraints: any[] = selectedCustomerId
      ? [where('customerId', '==', selectedCustomerId), orderBy('timestamp', dir), limit(pageSize)]
      : [orderBy('timestamp', dir), limit(pageSize)];
    const qRef = (query as any)(...base, ...constraints);
    const unsub = onSnapshot(qRef as any, (snap: any) => {
      const docs: any[] = snap?.docs ?? [];
      const rows: ActivityEvent[] = docs.map((d: any) => ({ id: d.id, ...(d.data() as any) }));
      setEvents(rows);
      setIsLoading(false);
    }, () => setIsLoading(false));
    return () => unsub();
  }, [selectedCustomerId, pageSize, sortBy]);

  // Fetch customer names for display (simple cache)
  useEffect(() => {
    const uniqueIds = Array.from(new Set(events.map((e) => e.customerId).filter(Boolean)));
    const missing = uniqueIds.filter((id) => !customerNameById[id]);
    if (missing.length === 0) return;

    (async () => {
      const updates: Record<string, string> = {};
      await Promise.all(missing.map(async (id) => {
        try {
          const ref = doc(db, 'customers', id);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data() as CustomerDoc;
            const display = data?.company ? `${data.company}${data.name ? ` (${data.name})` : ''}` : (data?.name || id);
            updates[id] = display;
            // Build search text including contacts
            const contactsText = Array.isArray(data.contacts) ? data.contacts.map(c => `${c?.name || ''} ${c?.email || ''} ${c?.phone || ''}`).join(' ') : '';
            const searchText = `${data?.name || ''} ${data?.company || ''} ${contactsText}`.toLowerCase();
            setCustomerSearchById(prev => ({ ...prev, [id]: searchText }));
          } else {
            updates[id] = id;
            setCustomerSearchById(prev => ({ ...prev, [id]: id.toLowerCase() }));
          }
        } catch {
          updates[id] = id;
          setCustomerSearchById(prev => ({ ...prev, [id]: id.toLowerCase() }));
        }
      }));
      if (Object.keys(updates).length > 0) setCustomerNameById((prev) => ({ ...prev, ...updates }));
    })();
  }, [events]);

  const rows = useMemo(() => events, [events]);
  const filteredRows = useMemo(() => {
    return rows.filter((e) => {
      if (selectedTypes.size > 0 && !selectedTypes.has(e.eventType)) return false;
      if (searchTerm) {
        const hay = `${e.title || ''} ${e.primaryEntity?.type || ''} ${e.primaryEntity?.id || ''}`.toLowerCase();
        const customerSearch = customerSearchById[e.customerId] || '';
        const needle = searchTerm.toLowerCase();
        if (!hay.includes(needle) && !customerSearch.includes(needle)) return false;
      }
      if (dateStart) {
        const d = new Date(dateStart);
        d.setHours(0,0,0,0);
        const ts = e.timestamp instanceof Timestamp ? e.timestamp.toDate() : (e.timestamp as any)?.toDate?.() || (typeof e.timestamp === 'object' && 'seconds' in (e.timestamp as any) ? new Date((e.timestamp as any).seconds * 1000) : null);
        if (ts && ts < d) return false;
      }
      if (dateEnd) {
        const d = new Date(dateEnd);
        d.setHours(23,59,59,999);
        const ts = e.timestamp instanceof Timestamp ? e.timestamp.toDate() : (e.timestamp as any)?.toDate?.() || (typeof e.timestamp === 'object' && 'seconds' in (e.timestamp as any) ? new Date((e.timestamp as any).seconds * 1000) : null);
        if (ts && ts > d) return false;
      }
      return true;
    });
  }, [rows, selectedTypes, searchTerm, dateStart, dateEnd]);

  // loadOlder removed

  const handleOpen = async (e: ActivityEvent) => {
    try {
      // Chat-entity rows no-op (2026-05).
      if (e.primaryEntity?.type === 'chat') return;
      if (e.primaryEntity?.type === 'email' && e.primaryEntity.id) {
        // Load email data and open in header email panel
        const emailRef = doc(db, 'emails', e.primaryEntity.id);
        const snap = await getDoc(emailRef);
        if (snap.exists()) {
          const data = snap.data() as any;
          const panelData: PanelEmailData = {
            // The whole doc first: mailbox / folder / direction / emailType / senderEmail
            // say whether this is mail the owner SENT (Reply then goes to its recipients
            // instead of back to him) and whether Outlook actions apply - hand-picking
            // fields here silently dropped them. The normalised fields below win.
            ...data,
            id: snap.id,
            subject: data.subject || 'No Subject',
            text: data.text || null,
            html: data.html || null,
            // Legacy docs store `from` as a string or a single {address,name} object. Collapsing
            // those to [] erased the only sender info, so Reply then fell through to the Cc list
            // and pre-addressed a Cc'd third party instead of the sender. parseAddresses keeps it.
            from: Array.isArray(data.from) ? data.from : parseAddresses(data.from),
            to: data.to,
            cc: data.cc,
            bcc: data.bcc,
            date: data.date,
            attachments: data.attachments || [],
            customerId: Array.isArray(data.customerId) ? data.customerId[0] : (data.customerId || null),
          };
          openEmailPanel('viewEmail', panelData);
          return;
        }
      }
      if (e.linkUrl) {
        // Fallback: direct navigation for other entity types/routes
        navigate(e.linkUrl);
      }
    } catch {}
  };

  const handleOpenTaskOrJob = async (e: ActivityEvent) => {
    try {
      if (e.primaryEntity?.type === 'task' && e.primaryEntity.id) {
        const taskRef = doc(db, 'tasks', e.primaryEntity.id);
        const snap = await getDoc(taskRef);
        const data = snap.exists() ? (snap.data() as any) : null;
        const jobId = data?.jobId;
        if (jobId) {
          // Fetch the job and open TaskModal in-place
          const jobSnap = await getDoc(doc(db, 'jobs', jobId));
          if (jobSnap.exists()) {
            const jd = jobSnap.data() as any;
            const job = {
              id: jobSnap.id,
              jobLabel: jd.jobLabel || 'Job',
              description: jd.description || '',
              customerId: Array.isArray(jd.customerId) ? jd.customerId : (jd.customerId ? [jd.customerId] : []),
              unbilledHours: jd.unbilledHours ?? 0,
              totalHours: jd.totalHours ?? 0,
              creationDate: jd.creationDate || '',
              dueDate: jd.dueDate || '',
              status: jd.status || 'Open',
              lastEdited: jd.lastEdited || new Date().toISOString(),
            };
            setSelectedJob(job);
            setInitialTaskId(e.primaryEntity.id);
            setShowTaskModal(true);
            return true;
          }
        }
      }
      if (e.primaryEntity?.type === 'job' && e.primaryEntity.id) {
        const jobSnap = await getDoc(doc(db, 'jobs', e.primaryEntity.id));
        if (jobSnap.exists()) {
          const jd = jobSnap.data() as any;
          const job = {
            id: jobSnap.id,
            jobLabel: jd.jobLabel || 'Job',
            description: jd.description || '',
            customerId: Array.isArray(jd.customerId) ? jd.customerId : (jd.customerId ? [jd.customerId] : []),
            unbilledHours: jd.unbilledHours ?? 0,
            totalHours: jd.totalHours ?? 0,
            creationDate: jd.creationDate || '',
            dueDate: jd.dueDate || '',
            status: jd.status || 'Open',
            lastEdited: jd.lastEdited || new Date().toISOString(),
          };
          setSelectedJob(job);
          setShowJobEditModal(true);
          return true;
        }
      }
    } catch {}
    return false;
  };

  const handleCopyEntityId = async (entityId?: string) => {
    if (!entityId) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(entityId);
        setCopiedEntityId(entityId);
        setTimeout(() => setCopiedEntityId(null), 1500);
      }
    } catch {}
  };

  return (
    <div className='container-fluid'>
      <div className='row'>
        <div className='col-12'>
          <div className='card'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>Customer Activity</span>
                <span className='text-muted mt-1 fw-semibold'>Latest events across customers</span>
              </h3>
              <div className='d-flex align-items-center gap-2 flex-wrap flex-md-nowrap' style={{rowGap: '0.5rem'}}>
                <select className='form-select form-select-sm' value={selectedCustomerId} onChange={(e)=>setSelectedCustomerId(e.target.value)}>
                  <option value=''>All Customers</option>
                  {customerListCache?.map((c)=> (
                    <option key={c.id} value={c.id}>{c.company ? `${c.company}${c.name ? ` (${c.name})` : ''}` : (c.name || c.id)}</option>
                  ))}
                </select>
                <select className='form-select form-select-sm' value={pageSize} onChange={(e)=>setPageSize(parseInt(e.target.value,10))}>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
                <select className='form-select form-select-sm' value={sortBy} onChange={(e)=>setSortBy(e.target.value as any)}>
                  <option value='date_desc'>Most recent first</option>
                  <option value='date_asc'>Oldest first</option>
                </select>
                <input className='form-control form-control-sm' placeholder='Search...' value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} />
                <select className='form-select form-select-sm' value='' onChange={(e)=>{
                  const v = e.target.value; if (!v) return; setSelectedTypes(prev => { const n = new Set(prev); if (n.has(v)) n.delete(v); else n.add(v); return n; }); e.currentTarget.selectedIndex = 0; }}>
                  <option value=''>Filter types…</option>
                  <option value='email.received'>Email Received</option>
                  <option value='email.sent'>Email Sent</option>
                  <option value='chat.activity'>Chat Activity</option>
                  <option value='job.created'>Job Created</option>
                  <option value='job.status'>Job Status</option>
                  <option value='task.created'>Task Created</option>
                  <option value='task.updated'>Task Updated</option>
                </select>
                <input type='date' className='form-control form-control-sm' value={dateStart} onChange={(e)=>setDateStart(e.target.value)} />
                <input type='date' className='form-control form-control-sm' value={dateEnd} onChange={(e)=>setDateEnd(e.target.value)} />
                {(selectedCustomerId || selectedTypes.size>0 || searchTerm || dateStart || dateEnd || pageSize !== 50 || sortBy !== 'date_desc') && (
                  <button className='btn btn-sm btn-light' onClick={()=>{ setSelectedCustomerId(''); setSelectedTypes(new Set()); setSearchTerm(''); setDateStart(''); setDateEnd(''); setPageSize(50); setSortBy('date_desc'); }}>Clear</button>
                )}
              </div>
            </div>
            <div className='card-body py-3'>
              {isLoading ? (
                <div className='text-center py-10'>Loading…</div>
              ) : (
                <div className='table-responsive'>
                  <table className='table align-middle table-row-dashed fs-6 gy-5'>
                    <thead>
                      <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                        <th>Time</th>
                        <th className='d-none d-md-table-cell'>Customer</th>
                        <th>Event</th>
                        <th>Title</th>
                        <th className='text-center'><i className='bi bi-tag'></i></th>
                        <th>Link</th>
                      </tr>
                    </thead>
                    <tbody className='text-gray-600 fw-semibold'>
                      {filteredRows.map((e) => (
                        <tr key={e.id}>
                          <td className='text-nowrap'>
                            <span className='d-inline d-md-none'>{formatRelative(e.timestamp)}</span>
                            <span className='d-none d-md-inline'>{formatDateTime(e.timestamp)}</span>
                          </td>
                          <td className='d-none d-md-table-cell'>{customerNameById[e.customerId] || e.customerId}</td>
                          <td>
                            <span className='badge badge-light-primary'>{getEventLabel(e.eventType)}</span>
                          </td>
                          <td className='text-truncate' style={{maxWidth: 320}} title={e.title || ''}>
                            <span className='d-none d-md-inline'>{e.title || ''}</span>
                            <span className='d-inline d-md-none'>{getMobileTitle(e.title || '')}</span>
                          </td>
                          <td className='text-center'>
                            <button
                              className='btn btn-sm btn-icon btn-light'
                              title={e.primaryEntity?.id || 'Copy ID'}
                              onClick={() => handleCopyEntityId(e.primaryEntity?.id)}
                            >
                              <i className='bi bi-tag'></i>
                            </button>
                            {copiedEntityId && copiedEntityId === e.primaryEntity?.id && (
                              <span className='badge badge-light-success ms-2'>Copied</span>
                            )}
                          </td>
                          <td className='d-flex gap-2'>
                            {!(e.primaryEntity?.type === 'task' || e.primaryEntity?.type === 'job') && (
                              <button className='btn btn-sm btn-light-primary' onClick={() => handleOpen(e)}>
                                Open
                              </button>
                            )}
                            {(e.primaryEntity?.type === 'task' || e.primaryEntity?.type === 'job') && (
                              <button className='btn btn-sm btn-light-primary' onClick={() => handleOpenTaskOrJob(e)}>
                                Open
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={6} className='text-center text-muted py-10'>No activity yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {/* Pagination button removed; page size and sort control the view */}
            </div>
          </div>
          {/* In-place modals for job/task */}
          {selectedJob && (
            <>
              <JobWorkspaceModal 
                job={selectedJob}
                show={showTaskModal}
                onHide={() => setShowTaskModal(false)}
              />
              <JobEditModal 
                show={showJobEditModal}
                job={selectedJob}
                onHide={() => setShowJobEditModal(false)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerActivityPage;

// Inline modals rendering
// Note: We append modals after export to keep component definition focused



